import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import {
  Key, Globe, Plus, Trash2, Copy, Check, Clock, AlertTriangle, Code, Webhook
} from 'lucide-react';

export default function ApiIntegration() {
  const navigate = useNavigate();
  const { axiosAuth } = useAuth();
  const [apiKeys, setApiKeys] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewKey, setShowNewKey] = useState(null);
  const [newWebhookUrl, setNewWebhookUrl] = useState('');
  const [showAddWebhook, setShowAddWebhook] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => { fetchData(); }, [axiosAuth]);

  const fetchData = async () => {
    try {
      const api = axiosAuth();
      const [keysRes, hooksRes] = await Promise.all([
        api.get('/admin/api-keys'),
        api.get('/admin/webhooks'),
      ]);
      setApiKeys(keysRes.data.api_keys);
      setWebhooks(hooksRes.data.webhooks);
    } catch (err) {
      if (err.response?.status === 403) { toast.error('Admin access required'); navigate('/'); }
      else toast.error('Failed to load data');
    } finally { setLoading(false); }
  };

  const createApiKey = async () => {
    try {
      const res = await axiosAuth().post('/admin/api-keys');
      setShowNewKey(res.data.api_key.key);
      fetchData();
    } catch { toast.error('Failed to create key'); }
  };

  const revokeApiKey = async (id) => {
    try {
      await axiosAuth().delete(`/admin/api-keys/${id}`);
      toast.success('API key revoked');
      setDeleteConfirm(null);
      fetchData();
    } catch { toast.error('Failed to revoke'); }
  };

  const createWebhook = async () => {
    if (!newWebhookUrl.trim()) { toast.error('URL is required'); return; }
    try {
      await axiosAuth().post('/admin/webhooks', { url: newWebhookUrl, events: ['simulation.completed'] });
      toast.success('Webhook created');
      setNewWebhookUrl('');
      setShowAddWebhook(false);
      fetchData();
    } catch { toast.error('Failed to create webhook'); }
  };

  const deleteWebhook = async (id) => {
    try {
      await axiosAuth().delete(`/admin/webhooks/${id}`);
      toast.success('Webhook deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(true);
    setTimeout(() => setCopiedKey(false), 2000);
    toast.success('Copied to clipboard');
  };

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="heritage-card p-8 text-center"><div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin mx-auto mb-4" /><p className="font-body text-sm">Loading...</p></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="api-integration-page">
      <Navbar showBack backTo="/admin" backLabel="API Integration" />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="mb-8">
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">API Integration</h1>
          <p className="font-body text-sm text-muted-foreground mt-1">Connect Simulify by Brainayan with your LMS or third-party tools via REST API</p>
        </div>

        {/* API Keys Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-base sm:text-lg font-bold flex items-center gap-2">
              <Key className="w-4 h-4 text-muted-foreground" /> API Keys
            </h2>
            <Button data-testid="create-api-key-btn" onClick={createApiKey}
              className="bg-brand text-[#333333] gap-1.5 h-8 px-3 text-xs font-medium rounded-lg hover:opacity-90">
              <Plus className="w-3.5 h-3.5" /> Generate Key
            </Button>
          </div>

          {apiKeys.length === 0 ? (
            <div className="heritage-card p-6 text-center">
              <Key className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
              <p className="font-body text-sm text-muted-foreground">No API keys yet. Generate one to get started.</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="api-keys-list">
              {apiKeys.map((k, i) => (
                <div key={k.id} className="heritage-card p-4 flex items-center gap-3" data-testid={`api-key-${i}`}>
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${k.active ? 'bg-success/10' : 'bg-muted'}`}>
                    <Key className={`w-4 h-4 ${k.active ? 'text-success' : 'text-muted-foreground'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <code className="font-mono text-xs truncate">{k.key_preview}</code>
                      {k.active ? (
                        <Badge className="bg-success/10 text-success border-none font-body text-[10px] px-1.5 py-0">Active</Badge>
                      ) : (
                        <Badge className="bg-muted text-muted-foreground border-none font-body text-[10px] px-1.5 py-0">Revoked</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 text-[10px] text-muted-foreground font-body">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(k.created_at).toLocaleDateString()}</span>
                      <span>{k.usage_count || 0} requests</span>
                      {k.last_used && <span>Last: {new Date(k.last_used).toLocaleDateString()}</span>}
                    </div>
                  </div>
                  {k.active && (
                    <button data-testid={`revoke-key-${i}`} onClick={() => setDeleteConfirm({ type: 'key', id: k.id })}
                      className="w-7 h-7 border border-border rounded-md flex items-center justify-center hover:bg-destructive/10 transition-colors" title="Revoke">
                      <Trash2 className="w-3 h-3 text-destructive" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Webhooks Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-base sm:text-lg font-bold flex items-center gap-2">
              <Globe className="w-4 h-4 text-muted-foreground" /> Webhooks
            </h2>
            <Button data-testid="add-webhook-btn" onClick={() => setShowAddWebhook(true)}
              className="bg-brand text-[#333333] gap-1.5 h-8 px-3 text-xs font-medium rounded-lg hover:opacity-90">
              <Plus className="w-3.5 h-3.5" /> Add Webhook
            </Button>
          </div>

          {webhooks.length === 0 ? (
            <div className="heritage-card p-6 text-center">
              <Globe className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
              <p className="font-body text-sm text-muted-foreground">No webhooks configured. Add one to receive simulation events.</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="webhooks-list">
              {webhooks.map((h, i) => (
                <div key={h.id} className="heritage-card p-4 flex items-center gap-3" data-testid={`webhook-${i}`}>
                  <div className="w-9 h-9 bg-info/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Globe className="w-4 h-4 text-info" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <code className="font-mono text-xs truncate block">{h.url}</code>
                    <div className="flex items-center gap-2 mt-0.5">
                      {h.events?.map(e => (
                        <Badge key={e} className="bg-muted text-muted-foreground border-none font-mono text-[10px] px-1.5 py-0">{e}</Badge>
                      ))}
                    </div>
                  </div>
                  <button data-testid={`delete-webhook-${i}`} onClick={() => deleteWebhook(h.id)}
                    className="w-7 h-7 border border-border rounded-md flex items-center justify-center hover:bg-destructive/10 transition-colors" title="Delete">
                    <Trash2 className="w-3 h-3 text-destructive" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* API Documentation */}
        <div>
          <h2 className="font-heading text-base sm:text-lg font-bold flex items-center gap-2 mb-4">
            <Code className="w-4 h-4 text-muted-foreground" /> API Reference
          </h2>
          <div className="heritage-card p-5 space-y-4" data-testid="api-docs">
            <div>
              <p className="font-body text-xs text-muted-foreground mb-2">Base URL</p>
              <div className="bg-muted rounded-lg px-3 py-2 flex items-center gap-2">
                <code className="font-mono text-sm flex-1">{API_URL}/api/v1</code>
                <button onClick={() => copyToClipboard(`${API_URL}/api/v1`)} className="w-7 h-7 border border-border rounded-md flex items-center justify-center hover:bg-surface transition-colors">
                  <Copy className="w-3 h-3 text-muted-foreground" />
                </button>
              </div>
            </div>

            <div>
              <p className="font-body text-xs text-muted-foreground mb-2">Authentication</p>
              <div className="bg-muted rounded-lg px-3 py-2">
                <code className="font-mono text-xs">X-API-Key: brn_your_api_key_here</code>
              </div>
            </div>

            <div>
              <p className="font-body text-xs font-semibold mb-2">Available Endpoints</p>
              <div className="space-y-1.5">
                {[
                  { method: 'GET', path: '/health', desc: 'Health check' },
                  { method: 'GET', path: '/scenarios', desc: 'List scenarios with variations & capabilities' },
                  { method: 'POST', path: '/simulations/start', desc: 'Start simulation (supports variation param)' },
                  { method: 'POST', path: '/simulations/{id}/message', desc: 'Send a message in a simulation' },
                  { method: 'POST', path: '/simulations/{id}/complete', desc: 'Complete and score a simulation' },
                  { method: 'GET', path: '/simulations/{id}', desc: 'Get simulation details and messages' },
                  { method: 'GET', path: '/users/{email}/simulations', desc: 'List all simulations for a user' },
                  { method: 'POST', path: '/users', desc: 'Create a new user' },
                  { method: 'POST', path: '/assignments', desc: 'Assign simulation to a user' },
                  { method: 'GET', path: '/reports/user/{email}', desc: 'Individual performance report' },
                  { method: 'GET', path: '/reports/team/{id}', desc: 'Team aggregated report' },
                  { method: 'GET', path: '/reports/org/{id}', desc: 'Organization report' },
                ].map((ep, i) => (
                  <div key={i} className="flex items-center gap-3 py-1.5 border-b border-border last:border-0">
                    <Badge className={`font-mono text-[10px] px-1.5 py-0 border-none ${ep.method === 'GET' ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'}`}>
                      {ep.method}
                    </Badge>
                    <code className="font-mono text-xs flex-1">{ep.path}</code>
                    <span className="font-body text-xs text-muted-foreground hidden sm:block">{ep.desc}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="font-body text-xs font-semibold mb-2">Example: Create User</p>
              <div className="bg-surface-2 rounded-lg p-3 overflow-x-auto">
                <pre className="font-mono text-[11px] whitespace-pre text-foreground">{`curl -X POST "${API_URL}/api/v1/users" \\
  -H "X-API-Key: brn_your_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "John Doe",
    "email": "john@company.com",
    "password": "securePass123",
    "role": "user",
    "org_id": "org-uuid-here",
    "vertical_id": "vert-uuid-here",
    "team_id": "team-uuid-here"
  }'`}</pre>
              </div>
              <p className="font-body text-[10px] text-muted-foreground mt-1.5">Fields <code className="font-mono text-[10px]">org_id</code>, <code className="font-mono text-[10px]">vertical_id</code>, <code className="font-mono text-[10px]">team_id</code> are optional. Get IDs from the admin hierarchy endpoints.</p>
            </div>

            <div>
              <p className="font-body text-xs font-semibold mb-2">Example: Start Simulation (with Simulator ID)</p>
              <div className="bg-surface-2 rounded-lg p-3 overflow-x-auto">
                <pre className="font-mono text-[11px] whitespace-pre text-foreground">{`curl -X POST "${API_URL}/api/v1/simulations/start" \\
  -H "X-API-Key: brn_your_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "user_email": "john@company.com",
    "scenario_id": "giving-feedback",
    "simulator_id": "LMS-SIM-2026-001",
    "difficulty": "intermediate",
    "variation": "peer-feedback",
    "language": "en",
    "user_role": "Engineering Manager",
    "partner_name": "Rahul",
    "partner_role": "Senior Developer",
    "company_name": "TechCorp"
  }'`}</pre>
              </div>
              <p className="font-body text-[10px] text-muted-foreground mt-1.5">
                <code className="font-mono text-[10px]">simulator_id</code> is your external tracking ID (optional) — stored with the simulation for cross-system reference. Use <code className="font-mono text-[10px]">GET /scenarios</code> to list available scenario IDs and variations.
              </p>
            </div>

            <div>
              <p className="font-body text-xs font-semibold mb-2">Speech-to-Text & Voice Capabilities</p>
              <div className="bg-surface-2 rounded-lg p-3">
                <p className="font-body text-xs leading-relaxed text-foreground mb-2">When simulations are accessed via the browser UI, the following voice features are available:</p>
                <ul className="space-y-1">
                  <li className="font-body text-xs text-muted-foreground flex items-start gap-2">
                    <Badge className="bg-success/10 text-success border-none text-[10px] px-1.5 py-0 flex-shrink-0 mt-0.5">STT</Badge>
                    <span><strong>Speech-to-Text</strong> - Users can speak and their voice is transcribed in real-time. Supported in Chrome, Edge, and Safari.</span>
                  </li>
                  <li className="font-body text-xs text-muted-foreground flex items-start gap-2">
                    <Badge className="bg-info/10 text-info border-none text-[10px] px-1.5 py-0 flex-shrink-0 mt-0.5">Voice</Badge>
                    <span><strong>Voice Level Visualization</strong> - Real-time audio level indicators during voice input.</span>
                  </li>
                  <li className="font-body text-xs text-muted-foreground flex items-start gap-2">
                    <Badge className="bg-warning/10 text-warning border-none text-[10px] px-1.5 py-0 flex-shrink-0 mt-0.5">Webcam</Badge>
                    <span><strong>Video Feed</strong> - Local webcam self-view during simulations for a realistic video call experience.</span>
                  </li>
                </ul>
                <p className="font-body text-[10px] text-muted-foreground mt-2">The <code className="font-mono text-[10px]">GET /scenarios</code> response includes a <code className="font-mono text-[10px]">capabilities</code> object describing available features.</p>
              </div>
            </div>

            <div>
              <p className="font-body text-xs font-semibold mb-2">Webhook Payload</p>
              <div className="bg-surface-2 rounded-lg p-3 overflow-x-auto">
                <pre className="font-mono text-[11px] whitespace-pre text-foreground">{`{
  "event": "simulation.completed",
  "data": {
    "simulation_id": "abc-123",
    "user_id": "user-456",
    "user_email": "user@company.com",
    "scenario_id": "giving-feedback",
    "scenario_title": "Giving Feedback",
    "variation": "peer-feedback",
    "variation_title": "Peer-to-Peer Feedback",
    "difficulty": "intermediate",
    "score": { "overall_score": 8.2, ... },
    "completed_at": "2026-02-15T10:30:00Z"
  },
  "timestamp": "2026-02-15T10:30:00Z"
}`}</pre>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* New Key Dialog */}
      <Dialog open={!!showNewKey} onOpenChange={(open) => !open && setShowNewKey(null)}>
        <DialogContent className="border border-border shadow-modal bg-surface max-w-md" data-testid="new-key-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold flex items-center gap-2"><Key className="w-5 h-5 text-success" /> API Key Created</DialogTitle>
            <DialogDescription className="font-body text-sm text-muted-foreground">
              Copy this key now. You won&apos;t be able to see it again.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            <div className="flex items-center gap-2 bg-muted rounded-lg p-3">
              <code className="font-mono text-xs flex-1 break-all" data-testid="new-key-value">{showNewKey}</code>
              <button data-testid="copy-key-btn" onClick={() => copyToClipboard(showNewKey)}
                className="w-8 h-8 border border-border rounded-lg flex items-center justify-center hover:bg-surface transition-colors flex-shrink-0">
                {copiedKey ? <Check className="w-4 h-4 text-success" /> : <Copy className="w-4 h-4 text-muted-foreground" />}
              </button>
            </div>
            <div className="flex items-start gap-2 mt-3 p-2 bg-warning/5 border border-warning/10 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-warning flex-shrink-0 mt-0.5" />
              <p className="font-body text-xs text-muted-foreground">Store this key securely. It cannot be retrieved after closing this dialog.</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Webhook Dialog */}
      <Dialog open={showAddWebhook} onOpenChange={setShowAddWebhook}>
        <DialogContent className="border border-border shadow-modal bg-surface max-w-md" data-testid="add-webhook-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold">Add Webhook</DialogTitle>
            <DialogDescription className="font-body text-sm text-muted-foreground">
              We&apos;ll send a POST request to this URL when simulations are completed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <input data-testid="webhook-url-input" type="url" value={newWebhookUrl}
              onChange={(e) => setNewWebhookUrl(e.target.value)}
              className="heritage-input w-full text-sm" placeholder="https://your-app.com/webhooks/brainayan" />
            <div className="flex gap-2">
              <Button onClick={() => setShowAddWebhook(false)}
                className="flex-1 h-9 bg-transparent border border-border text-foreground hover:bg-muted text-sm font-medium rounded-lg shadow-none">
                Cancel
              </Button>
              <Button data-testid="save-webhook-btn" onClick={createWebhook}
                className="flex-1 h-9 bg-brand text-[#333333] text-sm font-medium rounded-lg hover:opacity-90">
                Save Webhook
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <DialogContent className="border border-border shadow-modal bg-surface max-w-sm" data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold">Revoke API Key?</DialogTitle>
            <DialogDescription className="font-body text-sm text-muted-foreground">
              This key will be immediately deactivated. Any integrations using it will stop working.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 mt-4">
            <Button onClick={() => setDeleteConfirm(null)}
              className="flex-1 h-9 bg-transparent border border-border text-foreground hover:bg-muted text-sm font-medium rounded-lg shadow-none">Cancel</Button>
            <Button data-testid="confirm-revoke-btn" onClick={() => revokeApiKey(deleteConfirm?.id)}
              className="flex-1 h-9 bg-destructive text-white text-sm font-medium rounded-lg hover:opacity-90">Revoke</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
